import { createI18n } from 'vue-i18n';

type MessageTree = Record<string, unknown>;

const localeModules = import.meta.glob('./locale/fr/**/*.json', {
	eager: true,
	import: 'default',
}) as Record<string, MessageTree>;

const assignPath = (
	target: MessageTree,
	pathParts: string[],
	value: unknown
) => {
	let current = target;

	for (let pathIndex = 0; pathIndex < pathParts.length - 1; pathIndex += 1) {
		const pathPart = pathParts[pathIndex];
		const nextValue = current[pathPart];

		if (
			!nextValue ||
			typeof nextValue !== 'object' ||
			Array.isArray(nextValue)
		) {
			current[pathPart] = {};
		}

		current = current[pathPart] as MessageTree;
	}

	current[pathParts[pathParts.length - 1]] = value;
};

// Root bucket files expose several top-level namespaces (path is not a key prefix).
const ROOT_BUCKET_FILES = new Set(['enums', 'ui']);

const buildLocaleMessages = (
	modules: Record<string, MessageTree>
): MessageTree => {
	const messages: MessageTree = {};

	for (const [modulePath, moduleMessages] of Object.entries(modules)) {
		const relativePath = modulePath
			.replace(/^\.\/locale\/fr\//, '')
			.replace(/\.json$/, '');
		const pathParts = relativePath.split('/').filter(Boolean);

		if (pathParts.length === 0) continue;

		if (pathParts.length === 1 && ROOT_BUCKET_FILES.has(pathParts[0])) {
			Object.assign(messages, moduleMessages);
			continue;
		}

		assignPath(messages, pathParts, moduleMessages);
	}

	return messages;
};

const fr = buildLocaleMessages(localeModules);

// Custom function to replace JSON.stringify
// and avoid circular structure errors
// biome-ignore lint/suspicious/noExplicitAny: Init Biome
// biome-ignore lint/correctness/noUnusedVariables: Init Biome
function safeStringify(obj: any) {
	const seen = new WeakSet();
	return JSON.stringify(obj, (key, value) => {
		// Ignore properties starting with _
		if (key.startsWith('_') && key !== '_value') {
			return undefined;
		}

		// Handle circular references
		if (typeof value === 'object' && value !== null) {
			if (seen.has(value)) {
				return '[Circular]';
			}
			seen.add(value);
		}

		// Handle refs/computed
		if (
			value &&
			typeof value === 'object' &&
			typeof value.value !== 'undefined'
		) {
			return value.value;
		}

		return value;
	});
}

const i18n = createI18n({
	legacy: false,
	locale: import.meta.env.VITE_I18N_LOCALE || 'fr',
	fallbackLocale: import.meta.env.VITE_I18N_FALLBACK_LOCALE || 'fr',
	// Empty schema keeps vue-i18n ResourcePath inference shallow; messages are merged below.
	messages: {
		fr: {},
	},
	warnHtmlInMessage: 'off',
	escapeParameter: true,
	missingWarn: false,
	fallbackWarn: false,
});

i18n.global.mergeLocaleMessage('fr', fr);

export default i18n;
